<script setup>
import { ref, watch, computed } from "vue";
import { Head, router, useForm } from "@inertiajs/vue3";

import Datatables from "@/Shared/Tables/Datatables.vue";
import DatatableFooterWrapper from "@/Shared/Tables/DatatableFooterWrapper.vue";
import VHeaderBreadcrumb from "@/Shared/VHeaderBreadcrumb.vue";
import VAlert from "@/Shared/VAlert.vue";
import Swal from "sweetalert2";
import VueMultiselect from "vue-multiselect";

let props = defineProps({
    title: String,
    additional: Object,
});

const activeTab = ref("manual"); // 'manual' or 'csv'
const csvFileInput = ref(null);

const manualForm = useForm({
    document_template_id: "",
    category: "",
    tickets: "",
});

const csvForm = useForm({
    document_template_id: "",
    csv_file: null,
    csv_format: "category_ticket",
    default_category: "General",
});

const selectedTemplateManual = ref(null);
const selectedTemplateCsv = ref(null);

watch(selectedTemplateManual, (newVal) => {
    manualForm.document_template_id = newVal ? newVal.id : "";
});

watch(selectedTemplateCsv, (newVal) => {
    csvForm.document_template_id = newVal ? newVal.id : "";
});

const activeTemplateName = computed(() => {
    if (activeTab.value === 'manual' && selectedTemplateManual.value) {
        return selectedTemplateManual.value.name;
    } else if (activeTab.value === 'csv' && selectedTemplateCsv.value) {
        return selectedTemplateCsv.value.name;
    }
    return null;
});

const csvFormatOptions = [
    {
        value: "category_ticket",
        label: "Category + Ticket Code (2 columns)",
        description: "CSV with 2 columns: Category/Package in col 1, Ticket Code in col 2.",
    },
    {
        value: "multi_column",
        label: "Multi-Column Categories (header = categories)",
        description: "Header row contains category names (e.g. VIP, REGULAR). Each column lists ticket codes for that category.",
    },
    {
        value: "single_column",
        label: "Single Column (ticket codes only)",
        description: "One ticket code per row, single column.",
    }
];

const breadcrumbs = [
    {
        url: "#",
        label: "Inject Ticket",
    },
];

const changePageLength = (value) => {
    getList({ per_page: value });
};

const onFilter = (value) => {
    getList({
        search_fields: value.search_fields,
        search_values: value.search_values,
        per_page: props.additional.filters.per_page ?? 20,
        order_by: value.order_by,
        order_type: value.order_type,
    });
};

const getList = (params) => {
    router.get(props.additional.urlIndex, params, {
        preserveState: true,
        replace: true,
    });
};

const removeAction = async (value) => {
    const result = await Swal.fire({
        icon: "warning",
        title: "Are you sure?",
        text: "You won't be able to revert this ticket!",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) {
        return false;
    }

    router.post(value.url, { _method: "DELETE" });
};

const actionClick = (value) => {
    if (value.type == "delete") {
        removeAction(value);
    }
};

const submitManual = () => {
    manualForm.post(props.additional.urlStore, {
        onSuccess: () => {
            manualForm.reset("tickets");
        },
    });
};

const handleCsvFileChange = (e) => {
    csvForm.csv_file = e.target.files[0];
};

const submitCsv = () => {
    csvForm.post(props.additional.urlUploadCsv, {
        onSuccess: () => {
            csvForm.reset();
            selectedTemplateCsv.value = null;
            if (csvFileInput.value) {
                csvFileInput.value.value = "";
            }
        },
    });
};
</script>

<template>
    <Head>
        <title>{{ title }}</title>
    </Head>

    <div class="p-4 page-bg">
        <VHeaderBreadcrumb :breadcrumbs="breadcrumbs" />

        <VAlert class="mb-4" />

        <!-- Form Cards with gorgeous Tab styling -->
        <div class="card mb-5 border-0 custom-card">
            <div class="card-header bg-transparent border-0 pt-4 pb-0 px-4">
                <div class="tabs-container">
                    <div class="tabs-slider-wrapper">
                        <button
                            class="tab-btn fw-bold"
                            :class="{ active: activeTab === 'manual' }"
                            @click="activeTab = 'manual'"
                            type="button"
                        >
                            <i class="fas fa-keyboard me-2"></i>Manual Bulk Input
                        </button>
                        <button
                            class="tab-btn fw-bold"
                            :class="{ active: activeTab === 'csv' }"
                            @click="activeTab = 'csv'"
                            type="button"
                        >
                            <i class="fas fa-file-csv me-2"></i>CSV Bulk Upload
                        </button>
                    </div>
                </div>
            </div>

            <div class="card-body p-4 pt-3 bg-white">
                <!-- Tab: Manual Input -->
                <div v-if="activeTab === 'manual'" class="fade-in">
                    <div class="d-flex align-items-center mb-4">
                        <div class="section-icon-wrapper me-3">
                            <i class="fas fa-keyboard text-brand"></i>
                        </div>
                        <div>
                            <h5 class="fw-bold mb-1 text-dark-gray text-brand-accent">Manual Ticket Injection</h5>
                        </div>
                    </div>
                    
                    <form @submit.prevent="submitManual" class="mt-4">
                        <div class="row mb-4 align-items-center">
                            <label class="col-sm-3 col-form-label fw-bold text-end-sm label-brand">Document Template / Event <span class="text-danger">*</span></label>
                            <div class="col-sm-9">
                                <VueMultiselect
                                    v-model="selectedTemplateManual"
                                    label="name"
                                    track-by="id"
                                    placeholder="Type to search and select event template..."
                                    :options="additional.templates"
                                    open-direction="bottom"
                                />
                                <input type="hidden" v-model="manualForm.document_template_id" required />
                            </div>
                        </div>

                        <div class="row mb-4 align-items-center">
                            <label class="col-sm-3 col-form-label fw-bold text-end-sm label-brand">Category / Package</label>
                            <div class="col-sm-9">
                                <input
                                    v-model="manualForm.category"
                                    type="text"
                                    class="form-control custom-select-glow"
                                    placeholder="e.g. VIP, REGULAR, FESTIVAL (optional, defaults to General)"
                                />
                            </div>
                        </div>

                        <div class="row mb-4">
                            <label class="col-sm-3 col-form-label fw-bold text-end-sm label-brand pt-2">Ticket Code(s) <span class="text-danger">*</span></label>
                            <div class="col-sm-9">
                                <textarea
                                    v-model="manualForm.tickets"
                                    rows="6"
                                    class="form-control custom-select-glow code-textarea"
                                    placeholder="Enter ticket codes here. You can input multiple tickets by separating them with newlines or commas.&#10;Example:&#10;TKT-VIP-001&#10;TKT-VIP-002, TKT-VIP-003"
                                    required
                                ></textarea>
                            </div>
                        </div>

                        <div class="row mt-4">
                            <div class="col-sm-9 offset-sm-3">
                                <button
                                    type="submit"
                                    class="btn btn-premium px-4 py-2 border-0 shadow-sm"
                                    :disabled="manualForm.processing"
                                >
                                    <span v-if="manualForm.processing" class="spinner-border spinner-border-sm me-2"></span>
                                    <i v-else class="fas fa-paper-plane me-2"></i> Inject Ticket(s)
                                </button>
                            </div>
                        </div>
                    </form>
                </div>

                <!-- Tab: CSV Upload -->
                <div v-if="activeTab === 'csv'" class="fade-in">
                    <div class="d-flex align-items-center mb-4">
                        <div class="section-icon-wrapper me-3">
                            <i class="fas fa-file-csv text-brand"></i>
                        </div>
                        <div>
                            <h5 class="fw-bold mb-1 text-dark-gray text-brand-accent">CSV Ticket Injection</h5>
                        </div>
                    </div>

                    <form @submit.prevent="submitCsv" class="mt-4">
                        <div class="row mb-4 align-items-center">
                            <label class="col-sm-3 col-form-label fw-bold text-end-sm label-brand">Document Template / Event <span class="text-danger">*</span></label>
                            <div class="col-sm-9">
                                <VueMultiselect
                                    v-model="selectedTemplateCsv"
                                    label="name"
                                    track-by="id"
                                    placeholder="Type to search and select event template..."
                                    :options="additional.templates"
                                    open-direction="bottom"
                                />
                                <input type="hidden" v-model="csvForm.document_template_id" required />
                            </div>
                        </div>

                        <div class="row mb-4 align-items-center">
                            <label class="col-sm-3 col-form-label fw-bold text-end-sm label-brand">CSV Format <span class="text-danger">*</span></label>
                            <div class="col-sm-9">
                                <select
                                    v-model="csvForm.csv_format"
                                    class="form-select custom-select-glow"
                                    required
                                >
                                    <option v-for="opt in csvFormatOptions" :key="opt.value" :value="opt.value">
                                        {{ opt.label }}
                                    </option>
                                </select>
                                <div class="form-text text-muted mt-2">
                                    <i class="fas fa-info-circle me-1 text-brand"></i>
                                    {{ csvFormatOptions.find(o => o.value === csvForm.csv_format)?.description }}
                                </div>
                            </div>
                        </div>

                        <!-- Advanced: Default Category (for single_column) -->
                        <div class="row mb-4 align-items-center" v-if="csvForm.csv_format === 'single_column' || csvForm.csv_format === 'ticket_column'">
                            <label class="col-sm-3 col-form-label fw-bold text-end-sm label-brand">Default Category</label>
                            <div class="col-sm-9">
                                <input
                                    v-model="csvForm.default_category"
                                    type="text"
                                    class="form-control custom-select-glow"
                                    placeholder="e.g. General, VIP, Reguler"
                                />
                                <div class="form-text text-muted mt-2">
                                    <i class="fas fa-info-circle me-1 text-brand"></i> Used as a fallback when category is not specified in the CSV.
                                </div>
                            </div>
                        </div>

                        <div class="row mb-4 align-items-center">
                            <label class="col-sm-3 col-form-label fw-bold text-end-sm label-brand">CSV File <span class="text-danger">*</span></label>
                            <div class="col-sm-9">
                                <div class="custom-file-upload">
                                    <input
                                        ref="csvFileInput"
                                        type="file"
                                        accept=".csv,.txt"
                                        class="form-control custom-select-glow file-input-field"
                                        @change="handleCsvFileChange"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div class="row mt-4">
                            <div class="col-sm-9 offset-sm-3">
                                <button
                                    type="submit"
                                    class="btn btn-premium px-4 py-2 border-0 shadow-sm"
                                    :disabled="csvForm.processing"
                                >
                                    <span v-if="csvForm.processing" class="spinner-border spinner-border-sm me-2"></span>
                                    <i v-else class="fas fa-upload me-2"></i> Run Seeder & Import
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <!-- Ticket Listing Card -->
        <div class="card border-0 custom-card">
            <div class="card-header bg-transparent border-0 py-4 px-4 d-flex justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                    <div class="section-icon-wrapper me-3">
                        <i class="fas fa-list text-brand"></i>
                    </div>
                    <div>
                        <h5 class="fw-bold mb-1 text-dark-gray text-brand-accent">Injected Tickets</h5>
                    </div>
                </div>
            </div>
            <div class="card-body p-4 pt-0">
                <div class="dataTables_wrapper dt-bootstrap5 premium-table-wrapper">
                    <Datatables
                        :columns="additional.columns"
                        :pagination="additional.list"
                        :filters="additional.filters"
                        @onFilter="onFilter"
                        @onActionClick="actionClick"
                    />

                    <DatatableFooterWrapper
                        :pagination="additional.list.meta"
                        :filters="additional.filters"
                        @onChange="changePageLength"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Page Layout */
.page-bg {
    background-color: #f7f9fc;
    min-height: 100vh;
}

/* Premium Card Layout */
.custom-card {
    border-radius: 16px !important;
    background: #ffffff;
    box-shadow: 0 10px 30px rgba(138, 37, 41, 0.03), 0 1px 3px rgba(0, 0, 0, 0.01) !important;
    border: 1px solid rgba(138, 37, 41, 0.06) !important;
    overflow: hidden;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.custom-card:hover {
    box-shadow: 0 15px 35px rgba(138, 37, 41, 0.05), 0 1px 4px rgba(0, 0, 0, 0.01) !important;
}

/* Premium Section Icon */
.section-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background-color: rgba(138, 37, 41, 0.08);
}

.section-icon-wrapper i {
    font-size: 1.35rem;
}

.text-brand {
    color: #8a2529 !important;
}

.text-brand-accent {
    color: #2b2b2b;
    letter-spacing: -0.02em;
}

/* Segmented Custom Tabs */
.tabs-container {
    display: flex;
    justify-content: center;
    margin-bottom: 1rem;
}

.tabs-slider-wrapper {
    position: relative;
    display: flex;
    background: rgba(138, 37, 41, 0.05);
    padding: 6px;
    border-radius: 30px;
    border: 1px solid rgba(138, 37, 41, 0.1);
}

.tab-btn {
    position: relative;
    padding: 10px 24px;
    font-size: 0.9rem;
    color: #5f6368;
    background: transparent;
    border: 0;
    border-radius: 24px;
    cursor: pointer;
    z-index: 2;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    outline: none !important;
}

.tab-btn:hover {
    color: #8a2529;
}

.tab-btn.active {
    color: #ffffff;
    background: linear-gradient(135deg, #8a2529 0%, #aa3a3e 100%);
    box-shadow: 0 4px 12px rgba(138, 37, 41, 0.25);
}

/* Form Styles */
.label-brand {
    color: #495057;
    font-size: 0.9rem;
}

.custom-select-glow {
    border-radius: 8px;
    border: 1.5px solid rgba(0, 0, 0, 0.1);
    padding: 10px 14px;
    transition: all 0.25s ease-in-out;
    font-size: 0.95rem;
    color: #333;
}

.custom-select-glow:focus {
    border-color: #8a2529;
    box-shadow: 0 0 0 4px rgba(138, 37, 41, 0.12);
    outline: none;
    background-color: #fff;
}

.code-textarea {
    font-family: 'Courier New', Courier, monospace;
    font-size: 0.9rem;
    line-height: 1.5;
}



/* Premium Buttons */
.btn-premium {
    background: linear-gradient(135deg, #8a2529 0%, #aa3a3e 100%);
    color: #fff;
    font-weight: 600;
    border-radius: 8px;
    padding: 10px 24px;
    letter-spacing: 0.02em;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    box-shadow: 0 4px 10px rgba(138, 37, 41, 0.2);
}

.btn-premium:hover {
    background: linear-gradient(135deg, #741d20 0%, #8a2529 100%);
    color: #fff;
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(138, 37, 41, 0.3);
}

.btn-premium:active {
    transform: translateY(0);
}

.btn-premium:disabled {
    background: #dcdcdc;
    color: #8e8e8e;
    box-shadow: none;
    cursor: not-allowed;
    transform: none;
}

/* Datatable Wrappers styling */
.premium-table-wrapper :deep(table.dataTable) {
    border-collapse: separate;
    border-spacing: 0 8px;
    margin-top: 0 !important;
}

.premium-table-wrapper :deep(table.dataTable thead th) {
    background-color: #f8f9fa;
    color: #495057;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.05em;
    padding: 16px;
    border-bottom: none;
}

.premium-table-wrapper :deep(table.dataTable tbody tr) {
    background-color: #ffffff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.015);
    border-radius: 8px;
    transition: all 0.2s ease;
}

.premium-table-wrapper :deep(table.dataTable tbody tr:hover) {
    background-color: rgba(138, 37, 41, 0.01) !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(138, 37, 41, 0.04);
}

.premium-table-wrapper :deep(table.dataTable tbody td) {
    padding: 16px;
    border: none;
    color: #333;
    font-size: 0.9rem;
    vertical-align: middle;
}

/* Transitions */
.fade-in {
    animation: fadeIn 0.4s ease-in-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (min-width: 576px) {
    .text-end-sm {
        text-align: right;
    }
}

</style>

